import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { View, StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';

import { useA11ySelector } from '@sg/a11y-helpers';
import { HorizontalScrollRail, useResponsive, useDebounceFn } from '@sg/garnish';
import type { DeliveryOrderDetail, DeliveryOrderDetailInput, Product } from '@sg/graphql-schema';

import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { BagUpsellProductOption } from '../BagUpsellProductOption';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The rail that contains the upsell product cards for the bag.
 */
export const BagUpsellRail = (props: BagUpsellRailProps) => {
  const {
    upsellIdsBeingAdded,
    upsellProducts,
    deliveryOrderDetail,
    addUpsellToBag,
    isAddingUpsellToBag,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
  } = props;

  const a11ySelector = useA11ySelector('bag.upsell-products.container');
  const { minWidth } = useResponsive();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  // ─── Stepper Interactions ──────────────────────────────────────────────────

  const updateOpenSteppers = useCallback(
    (productId: string, isStepperShown: boolean) => {
      if (isStepperShown) {
        startChangingLineItemQuantity(productId);
        return;
      }

      stopChangingLineItemQuantity(productId);
    },
    [startChangingLineItemQuantity, stopChangingLineItemQuantity],
  );

  // ─── Telemetry ───────────────────────────────────────────────────────────────────

  useTrackEffect('bag_upsell_rail.view');

  const handleScrollEvent = useCallback(() => {
    track('bag_upsell_rail.scrolled');
  }, [track]);

  const debouncedHandleScroll = useDebounceFn(handleScrollEvent, 500);

  // ─────────────────────────────────────────────────────────────────────────────

  const deliveryDetails = useMemo<DeliveryOrderDetailInput | undefined>(() => {
    const addressId = deliveryOrderDetail?.address?.id;

    if (!addressId) return;

    return {
      addressId,
      vendor: deliveryOrderDetail.vendor,
      vendorRestaurantId: deliveryOrderDetail.vendorRestaurantId,
      deliveryFee: deliveryOrderDetail.deliveryFee,
      tip: deliveryOrderDetail.tip,
    };
  }, [
    deliveryOrderDetail?.address?.id,
    deliveryOrderDetail?.vendor,
    deliveryOrderDetail?.vendorRestaurantId,
    deliveryOrderDetail?.deliveryFee,
    deliveryOrderDetail?.tip,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} {...a11ySelector.props}>
      <HorizontalScrollRail
        withoutHeaderBorder
        heading={formatMessage(messages.header)}
        titleStyle={styles.title}
        showNavControls={minWidth.isSM}
        gap={theme.spacing['2']}
        outerOffset={-theme.spacing['4']}
        countPosition={minWidth.isSM ? 'left' : 'right'}
        onScroll={debouncedHandleScroll}
      >
        {upsellProducts.map((product) => {
          const isActive = upsellIdsBeingAdded.includes(product.id);

          return (
            <BagUpsellProductOption
              key={product.id}
              product={product}
              size={minWidth.isSM ? 'md' : 'sm'}
              addLineItemToCart={addUpsellToBag}
              isAddingToCart={isAddingUpsellToBag && isActive}
              isDisabled={upsellIdsBeingAdded.length > 0 && !isActive}
              updateOpenSteppers={updateOpenSteppers}
              deliveryDetails={deliveryDetails}
            />
          );
        })}
      </HorizontalScrollRail>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  header: {
    defaultMessage: 'You might also like',
    description: 'Bag Upsell Rail | Container Header',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: theme.spacing['2'],
    marginHorizontal: -theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  title: {
    marginTop: theme.spacing['1'],
    fontWeight: 'bold',
    fontSize: 18,
    color: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagUpsellRailProps = {
  upsellProducts: UpsellProduct[];
  upsellIdsBeingAdded: string[];
  deliveryOrderDetail: DeliveryOrderDetail | undefined;
  addUpsellToBag: (payload: {
    product: Partial<Product>;
    deliveryDetails?: DeliveryOrderDetailInput;
  }) => void;
  isAddingUpsellToBag: boolean;
  startChangingLineItemQuantity: (lineItemId: string) => void;
  stopChangingLineItemQuantity: (lineItemId: string) => void;
};

type UpsellProduct = Readonly<{
  id: string;
  name: string;
  cost: number;
  imageUrl: string;
  outOfStock: boolean;
  menuId: number;
}>;
